export type Lang = 'es' | 'en';

export const LANGS: Lang[] = ['es', 'en'];

/** Each language is named in its own language, the way a switcher should. */
export const LANGUAGE_NAME: Record<Lang, string> = { es: 'Español', en: 'English' };

const es = {
  nav: {
    home: 'Inicio',
    list: 'Mis cortitos',
    account: 'Mi cuenta',
    signedIn: 'Sesión iniciada',
    signOut: 'Salir',
    signIn: 'Entrar',
    withGoogle: 'Con Google',
    withFacebook: 'Con Facebook',
    withX: 'Con X',
    signInBlocked:
      'No se pudo abrir la ventana para entrar. Si estás en la app instalada del iPhone, entrá desde corti.to en el navegador.',
    close: 'Cerrar',
    coffee: 'Invitame un cafecito',
    api: 'API y agentes',
    dev: 'DEV',
    devNote: 'Versión de desarrollo',
    devHint: 'Los cortitos de acá no son los de corti.to',
  },
  theme: {
    label: (name: string) => `Tema: ${name}`,
    light: 'claro',
    dark: 'oscuro',
    system: 'sistema',
  },
  language: {
    label: (name: string) => `Idioma: ${name}`,
  },
  home: {
    heroTop: 'Acortá',
    heroLead: 'tu',
    heroWord: 'link',
    tagline: 'Pegá algo largo · ponele nombre o no · listo',
    longUrlLabel: 'Link largo',
    longUrlPlaceholder: 'https://ejemplo.com/una/ruta/muy/larga',
    longUrlMissing: 'Falta el link',
    longUrlInvalid: 'Ese link no parece válido',
    aliasLabel: 'Tu cortito',
    aliasPlaceholder: 'nombre-corto',
    aliasOptional: 'Si lo dejás vacío, se inventa uno',
    aliasSuggest: 'Dame uno',
    aliasReserved: 'Ese nombre lo usa corti.to',
    aliasInvalid: 'Hasta 20 caracteres. Sin espacios ni / , ; o #',
    aliasChecking: 'Revisando…',
    aliasFree: 'Está libre',
    aliasTaken: 'Ese nombre ya está usado',
    utmToggle: 'Parámetros de campaña',
    utmHint: 'Se agregan al destino antes de guardarlo',
    utmSource: 'Origen',
    utmSourcePlaceholder: 'newsletter',
    utmMedium: 'Medio',
    utmMediumPlaceholder: 'email',
    utmCampaign: 'Campaña',
    utmCampaignPlaceholder: 'lanzamiento',
    limitsToggle: 'Vencimiento y tope de visitas',
    limitsHint: 'Cuando se cumple cualquiera de los dos, el cortito deja de funcionar',
    expiresOn: 'Vence el',
    maxClicks: 'Tope de visitas',
    maxClicksPlaceholder: '50',
    tags: 'Etiquetas',
    tagsHint: 'Separadas por coma, para agruparlos después en la lista',
    tagsPlaceholder: 'trabajo, campaña',
    passwordToggle: 'Proteger con clave',
    passwordHint: 'La clave no se envía ni se guarda. Sin ella, el destino queda cerrado.',
    password: 'Clave',
    passwordPlaceholder: '8 caracteres o más',
    passwordInvalid: 'Usá entre 8 y 128 caracteres',
    submit: '¡Acortá!',
    created: 'Listo',
    previewHint: 'Agregale un + al final para ver los detalles antes de abrirlo',
    copy: 'Copiar el cortito',
    taken: 'Ese cortito ya está usado',
    invalid: 'No se puede acortar un cortito',
    dangerous: 'Google marca ese destino como peligroso',
    bounces: 'Ese destino redirige de vuelta a corti.to',
    error: 'No se pudo guardar. Probá de nuevo.',
  },
  list: {
    titleLead: 'Mis',
    titleWord: 'cortitos',
    signedOut: 'Entrá con tu cuenta para ver tus cortitos.',
    empty: 'Todavía no tenés ningún cortito.',
    summaryTotal: 'Cortitos',
    summaryVisits: 'Visitas',
    summaryBusiest: 'El más usado',
    summaryUnused: 'Sin usar',
    summaryDaily: 'Últimos 30 días',
    chartLabel: (alias: string, visits: number) =>
      `Visitas de ${alias} en los últimos 30 días: ${visits}`,
    chartLabelAll: (visits: number) =>
      `Visitas de todos tus cortitos en los últimos 30 días: ${visits}`,
    search: 'Buscar por cortito o destino',
    columnAlias: 'Cortito',
    columnClicks: 'Visitas',
    actions: (alias: string) => `Acciones de ${alias}`,
    copy: 'Copiar',
    menuStats: 'Visitas',
    menuPreview: 'Detalles',
    menuQr: 'QR',
    remove: 'Borrar',
    removeTitle: (alias: string) => `¿Borrar ${alias}?`,
    removeHint: 'El link deja de funcionar. Tenés unos segundos para deshacer la acción.',
    removeCancel: 'Dejalo',
    removeConfirm: 'Borrarlo',
    expired: 'Vencido',
    revive: 'Revivir',
    paused: 'Pausado',
    pause: 'Pausar',
    unpause: 'Reactivar',
    pausedHint: 'Pausado no lleva a ningún lado, pero el nombre sigue siendo tuyo',
    tags: 'Etiquetas',
    edit: 'Destino',
    editTitle: (alias: string) => `Destino de ${alias}`,
    editHint: 'El nombre del cortito y sus visitas quedan como están',
    editSave: 'Guardar',
    editSaved: 'Ahora apunta a otro lado',
    editClose: 'Cerrar',
    tagsTitle: (alias: string) => `Etiquetas de ${alias}`,
    tagsEmpty: 'Todavía no tiene ninguna',
    tagsClose: 'Cerrar',
    tagAdd: 'Agregar',
    tagRemove: (tag: string) => `Sacar ${tag}`,
    tagPlaceholder: 'trabajo',
    filterByTag: 'Filtrar por etiqueta',
    noMatches: 'Nada coincide con la búsqueda',
    removed: (alias: string) => `Borraste ${alias}`,
    undo: 'Deshacer',
  },
  install: {
    offer: 'Tenela a mano en el teléfono',
    action: 'Instalar',
    dismiss: 'Ahora no',
    stepsTitle: 'Agregala a tu inicio',
    share: 'Tocá Compartir, abajo',
    addToHome: 'Elegí "Agregar a inicio"',
    close: 'Cerrar',
  },
  stats: {
    action: 'Ver las visitas',
    title: (alias: string) => `Visitas de ${alias}`,
    window: (days: number) => `Últimos ${days} días`,
    chart: (alias: string, visits: number) =>
      `Visitas de ${alias} en los últimos 30 días: ${visits}`,
    busiest: (day: string, visits: number) =>
      `El día más movido fue el ${day}, con ${visits === 1 ? '1 visita' : `${visits} visitas`}`,
    empty: 'Nadie lo abrió en este tiempo',
    sinceNote:
      'Los días se cuentan desde que corti.to empezó a guardarlos, así que pueden ser menos que el total de visitas.',
    close: 'Cerrar',
    map: (alias: string) => `Países desde donde abrieron ${alias}`,
    countries: 'Países',
    devices: 'Dispositivos',
    sites: 'De dónde llegaron',
    device: { mobile: 'Celular', tablet: 'Tablet', desktop: 'Escritorio' },
    direct: 'Directo',
    otherSite: 'Otros sitios',
    unknownCountry: 'Sin país',
    rest: (visits: number) => `+ ${visits} más`,
    noOrigin: 'Todavía no se guardó nada sobre el origen de las visitas',
    originNote:
      'Sólo se guardan el país, el tipo de dispositivo y el sitio de origen, sumados. Ninguna visita queda registrada por separado.',
  },
  share: {
    action: 'Compartir',
    title: (alias: string) => `Compartir ${alias}`,
    on: (name: string) => `Compartir por ${name}`,
    native: 'Compartir con otra app',
    close: 'Cerrar',
  },
  qr: {
    action: 'Ver el QR',
    title: (alias: string) => `QR de ${alias}`,
    download: (format: string) => `Descargar ${format}`,
    copy: 'Copiar la imagen',
    copied: 'Copiada',
    close: 'Cerrar',
  },
  preview: {
    action: 'Ver detalles',
    title: (alias: string) => `Detalles de ${alias}`,
    pageTitle: 'Detalles',
    shortLink: 'El cortito',
    goesTo: 'Destino',
    visits: (count: number) => (count === 1 ? '1 visita' : `${count} visitas`),
    open: 'Abrir destino',
    copy: 'Copiar el destino',
    copied: 'Copiado',
    dangerous: 'Google marca este destino como peligroso',
    notCounted: 'Ver los detalles no suma una visita',
    protected: 'Escribí la clave para abrir el cortito',
    withPassword: 'Con clave',
    withoutPassword: 'Sin clave',
    passwordLabel: 'Clave',
    passwordPlaceholder: 'Escribí la clave',
    unlock: 'Abrir',
    wrongPassword: 'Clave incorrecta',
    tooManyAttempts: 'Demasiados intentos. Probá de nuevo en un rato.',
    close: 'Cerrar',
  },
  gate: {
    here: 'Estás en dev',
    lead: 'Esta es la versión de desarrollo. Los cortitos de acá no son los de corti.to y se borran cuando haga falta. Poné la clave para entrar.',
    label: 'Clave de dev',
    placeholder: 'clave',
    enter: 'Entrar',
    refused: 'Clave incorrecta',
    real: 'Ir a corti.to',
  },
  api: {
    open: 'API y agentes',
    title: 'API y agentes',
    lead: 'Con un token podés crear, ver y borrar cortitos desde la terminal o desde un agente de IA, sin abrir el navegador.',
    base: 'Dirección',
    limit: (calls: number) => `${calls} llamadas por hora por token`,
    docs: 'Cómo se usa',
    tokens: 'Tus tokens',
    none: 'Todavía no tenés ninguno.',
    nameLabel: 'Nombre',
    namePlaceholder: 'mi script',
    create: 'Crear un token',
    full: (most: number) => `Podés tener hasta ${most} tokens. Revocá uno para crear otro.`,
    shownOnce: 'Copialo ahora: no se vuelve a mostrar.',
    copy: 'Copiar',
    copied: 'Copiado',
    revoke: 'Revocar',
    sure: '¿Seguro?',
    revokeLabel: (name: string) => `Revocar ${name}`,
    lastUsed: (when: string) => `último uso: ${when}`,
    never: 'sin usar',
    callsMade: (calls: number) => (calls === 1 ? '1 llamada' : `${calls} llamadas`),
    failed: 'No se pudo crear el token. Probá de nuevo.',
    agents: 'Para tu agente',
    agentsLead: 'Copiá esto y pegáselo a Claude, a ChatGPT o al agente que uses.',
    copyPrompt: 'Copiar para tu agente',
    yourToken: 'tu-token',
    agentPrompt: (base: string, token: string) => `Podés acortar links con corti.to.

Dirección: ${base}
Token: ${token}
Mandá siempre el header Authorization: Bearer <token>

POST /links {"url":"https://…"} → crea uno y devuelve shortUrl
  opcionales: "alias", "expiresAt": "2026-12-31", "maxClicks": 10, "tags": ["a","b"]
GET /links → los míos
GET /links/<alias>?stats=1 → uno, con las visitas por día
DELETE /links/<alias> → lo borra
GET /me → de quién es el token y cuántas llamadas le quedan

Ejemplo:
curl -X POST ${base}/links \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://angular.dev"}'

Todo el detalle: ${base.replace('/api/v1', '/llms.txt')}`,
    close: 'Cerrar',
  },
  notFound: {
    title: 'Ese cortito no existe',
    body: 'El link que buscás no está en corti.to',
    action: 'Crear uno',
  },
  titles: {
    home: 'corti.to',
    list: 'Mis cortitos · corti.to',
    notFound: 'No existe · corti.to',
    preview: 'Detalles · corti.to',
  },
  description: 'Acortá tus links con corti.to',
};

/** Spanish is the shape every other language has to match. */
export type Messages = typeof es;
export type TitleKey = keyof Messages['titles'];

const en: Messages = {
  nav: {
    home: 'Home',
    list: 'My links',
    account: 'My account',
    signedIn: 'Signed in',
    signOut: 'Sign out',
    signIn: 'Sign in',
    withGoogle: 'With Google',
    withFacebook: 'With Facebook',
    withX: 'With X',
    signInBlocked:
      "The sign-in window could not open. If you're using the installed app on iPhone, sign in from corti.to in your browser.",
    close: 'Close',
    coffee: 'Buy me a coffee',
    api: 'API and agents',
    dev: 'DEV',
    devNote: 'Development version',
    devHint: "The links here aren't the ones on corti.to",
  },
  theme: {
    label: (name: string) => `Theme: ${name}`,
    light: 'light',
    dark: 'dark',
    system: 'system',
  },
  language: {
    label: (name: string) => `Language: ${name}`,
  },
  home: {
    heroTop: 'Shorten',
    heroLead: 'your',
    heroWord: 'link',
    tagline: 'Paste something long · name it or not · done',
    longUrlLabel: 'Long link',
    longUrlPlaceholder: 'https://example.com/a/very/long/path',
    longUrlMissing: 'The link is missing',
    longUrlInvalid: 'That link does not look valid',
    aliasLabel: 'Your short link',
    aliasPlaceholder: 'short-name',
    aliasOptional: 'Leave it empty and one is made up for you',
    aliasSuggest: 'Pick one for me',
    aliasReserved: 'That name is reserved by corti.to',
    aliasInvalid: 'Up to 20 characters. No spaces or / , ; #',
    aliasChecking: 'Checking…',
    aliasFree: 'It is free',
    aliasTaken: 'That name is already taken',
    utmToggle: 'Campaign parameters',
    utmHint: 'Added to the destination before it is saved',
    utmSource: 'Source',
    utmSourcePlaceholder: 'newsletter',
    utmMedium: 'Medium',
    utmMediumPlaceholder: 'email',
    utmCampaign: 'Campaign',
    utmCampaignPlaceholder: 'launch',
    limitsToggle: 'Expiry and visit limit',
    limitsHint: 'Once either is reached, the link stops working',
    expiresOn: 'Expires on',
    maxClicks: 'Visit limit',
    maxClicksPlaceholder: '50',
    tags: 'Labels',
    tagsHint: 'Comma separated, to group them later in the list',
    tagsPlaceholder: 'work, campaign',
    passwordToggle: 'Protect with a password',
    passwordHint: 'The password is not sent or stored. Without it, the destination stays closed.',
    password: 'Password',
    passwordPlaceholder: '8 characters or more',
    passwordInvalid: 'Use between 8 and 128 characters',
    submit: 'Shorten',
    created: 'Done',
    previewHint: 'Add a + at the end to see its details before opening it',
    copy: 'Copy the short link',
    taken: 'That name is already taken',
    invalid: "You can't shorten a corti.to link",
    dangerous: 'Google flags that destination as dangerous',
    bounces: 'That destination loops back to corti.to',
    error: "Couldn't save the link. Try again.",
  },
  list: {
    titleLead: 'My',
    titleWord: 'links',
    signedOut: 'Sign in to see your links.',
    empty: 'You do not have any links yet.',
    summaryTotal: 'Links',
    summaryVisits: 'Visits',
    summaryBusiest: 'Most used',
    summaryUnused: 'Never used',
    summaryDaily: 'Last 30 days',
    chartLabel: (alias: string, visits: number) =>
      `Visits to ${alias} over the last 30 days: ${visits}`,
    chartLabelAll: (visits: number) => `Visits to all your links over the last 30 days: ${visits}`,
    search: 'Search by link or destination',
    columnAlias: 'Link',
    columnClicks: 'Visits',
    actions: (alias: string) => `Actions for ${alias}`,
    copy: 'Copy',
    menuStats: 'Visits',
    menuPreview: 'Details',
    menuQr: 'QR',
    remove: 'Delete',
    removeTitle: (alias: string) => `Delete ${alias}?`,
    removeHint: "The link will stop working. You'll have a few seconds to undo.",
    removeCancel: 'Keep it',
    removeConfirm: 'Delete it',
    expired: 'Expired',
    revive: 'Revive',
    paused: 'Paused',
    pause: 'Pause',
    unpause: 'Resume',
    pausedHint: 'A paused link goes nowhere, but the name stays yours',
    tags: 'Labels',
    edit: 'Destination',
    editTitle: (alias: string) => `Destination of ${alias}`,
    editHint: 'The short name and its visits stay as they are',
    editSave: 'Save',
    editSaved: 'Destination updated',
    editClose: 'Close',
    tagsTitle: (alias: string) => `Labels on ${alias}`,
    tagsEmpty: 'No labels yet',
    tagsClose: 'Close',
    tagAdd: 'Add',
    tagRemove: (tag: string) => `Remove ${tag}`,
    tagPlaceholder: 'work',
    filterByTag: 'Filter by label',
    noMatches: 'No matches',
    removed: (alias: string) => `You deleted ${alias}`,
    undo: 'Undo',
  },
  install: {
    offer: 'Keep it handy on your phone',
    action: 'Install',
    dismiss: 'Not now',
    stepsTitle: 'Add it to your home screen',
    share: 'Tap Share below',
    addToHome: 'Choose "Add to Home Screen"',
    close: 'Close',
  },
  stats: {
    action: 'View visits',
    title: (alias: string) => `Visits to ${alias}`,
    window: (days: number) => `Last ${days} days`,
    chart: (alias: string, visits: number) => `Visits to ${alias} over the last 30 days: ${visits}`,
    busiest: (day: string, visits: number) =>
      `The busiest day was ${day}, with ${visits === 1 ? '1 visit' : `${visits} visits`}`,
    empty: 'Nobody opened it in that time',
    sinceNote:
      'Daily counts start from when corti.to began recording them, so they may add up to less than the total visits.',
    close: 'Close',
    map: (alias: string) => `Countries ${alias} was opened from`,
    countries: 'Countries',
    devices: 'Devices',
    sites: 'Where they came from',
    device: { mobile: 'Phone', tablet: 'Tablet', desktop: 'Desktop' },
    direct: 'Direct',
    otherSite: 'Other sites',
    unknownCountry: 'No country',
    rest: (visits: number) => `+ ${visits} more`,
    noOrigin: 'No data yet on where visits came from',
    originNote:
      'Only the country, device type, and referring site are stored as aggregated counts. No individual visit is recorded.',
  },
  share: {
    action: 'Share',
    title: (alias: string) => `Share ${alias}`,
    on: (name: string) => `Share on ${name}`,
    native: 'Share with another app',
    close: 'Close',
  },
  qr: {
    action: 'View QR',
    title: (alias: string) => `QR for ${alias}`,
    download: (format: string) => `Download ${format}`,
    copy: 'Copy the image',
    copied: 'Copied',
    close: 'Close',
  },
  preview: {
    action: 'View details',
    title: (alias: string) => `Details for ${alias}`,
    pageTitle: 'Details',
    shortLink: 'The short link',
    goesTo: 'Destination',
    visits: (count: number) => (count === 1 ? '1 visit' : `${count} visits`),
    open: 'Open destination',
    copy: 'Copy the destination',
    copied: 'Copied',
    dangerous: 'Google flags this destination as dangerous',
    notCounted: 'Viewing details does not count as a visit',
    protected: 'Enter the password to open the short link',
    withPassword: 'Password protected',
    withoutPassword: 'No password',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter the password',
    unlock: 'Open',
    wrongPassword: 'Incorrect password',
    tooManyAttempts: 'Too many attempts. Try again later.',
    close: 'Close',
  },
  gate: {
    here: 'You are on dev',
    lead: "This is the development version. The links here aren't the ones on corti.to and may be wiped when needed. Enter the key to continue.",
    label: 'Dev key',
    placeholder: 'key',
    enter: 'Enter',
    refused: 'Incorrect key',
    real: 'Go to corti.to',
  },
  api: {
    open: 'API and agents',
    title: 'API and agents',
    lead: 'With a token you can create, read, and delete links from a terminal or an AI agent, without opening a browser.',
    base: 'Address',
    limit: (calls: number) => `${calls} calls an hour per token`,
    docs: 'How to use it',
    tokens: 'Your tokens',
    none: 'You have none yet.',
    nameLabel: 'Name',
    namePlaceholder: 'my script',
    create: 'Create a token',
    full: (most: number) => `You can have up to ${most} tokens. Revoke one to create another.`,
    shownOnce: 'Copy it now: it is not shown again.',
    copy: 'Copy',
    copied: 'Copied',
    revoke: 'Revoke',
    sure: 'Sure?',
    revokeLabel: (name: string) => `Revoke ${name}`,
    lastUsed: (when: string) => `last used: ${when}`,
    never: 'never used',
    callsMade: (calls: number) => (calls === 1 ? '1 call' : `${calls} calls`),
    failed: "Couldn't create the token. Try again.",
    agents: 'For your agent',
    agentsLead: 'Copy this and paste it into Claude, ChatGPT, or whichever agent you use.',
    copyPrompt: 'Copy for your agent',
    yourToken: 'your-token',
    agentPrompt: (base: string, token: string) => `You can shorten links with corti.to.

Address: ${base}
Token: ${token}
Always send the header Authorization: Bearer <token>

POST /links {"url":"https://…"} → creates one and returns shortUrl
  optional: "alias", "expiresAt": "2026-12-31", "maxClicks": 10, "tags": ["a","b"]
GET /links → lists mine
GET /links/<alias>?stats=1 → one, with its visits per day
DELETE /links/<alias> → deletes it
GET /me → who the token belongs to and how many calls it has left

Example:
curl -X POST ${base}/links \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://angular.dev"}'

Full docs: ${base.replace('/api/v1', '/llms.txt')}`,
    close: 'Close',
  },
  notFound: {
    title: 'That link does not exist',
    body: 'The link you are trying to open is not on corti.to',
    action: 'Create one',
  },
  titles: {
    home: 'corti.to',
    list: 'My links · corti.to',
    notFound: 'Not found · corti.to',
    preview: 'Details · corti.to',
  },
  description: 'Shorten your links with corti.to',
};

export const MESSAGES: Record<Lang, Messages> = { es, en };
